/**
 * ClubSettingsInvitesPage - Invite Management for Club Leaders
 * Issue #1015: Manage Invites page for club leaders
 *
 * Features:
 * - View all invites (active, expired, revoked)
 * - Usage stats for multi-use invites
 * - Copy shareable link
 * - Revoke pending invites
 * - Create new invites
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClub } from '../../hooks/useClubs';
import { TopBar, Button, Card, Skeleton } from '../../components';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Link2, Plus, Copy, Check, Ban, Calendar, Mail, Phone, Users } from 'lucide-react';
import { invitesApi, type ClubInvite } from '../../lib/api/invites';
import { isRevocableInvite } from '../../lib/inviteRevocable';
import { toast } from 'sonner';

export default function ClubSettingsInvitesPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Data fetching
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isLeader = membership?.role === 'leader' || membership?.is_owner;

  // Fetch club invites
  const { data: invitesData, isLoading: invitesLoading } = useQuery({
    queryKey: ['club', club?.id, 'invites'],
    queryFn: () => invitesApi.listClubInvites(club!.id),
    enabled: !!club?.id && isLeader
  });
  const invites = invitesData?.invites || [];

  // Revoke mutation
  const revokeMutation = useMutation({
    mutationFn: (inviteId: string) => invitesApi.revokeClubInvite(club!.id, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club', club?.id, 'invites'] });
      toast.success('Invite revoked');
      setInviteToRevoke(null);
    },
    onError: () => {
      toast.error('Failed to revoke invite');
    }
  });

  // UI state
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [inviteToRevoke, setInviteToRevoke] = useState<string | null>(null);

  const handleCopyLink = async (inviteId: string, inviteCode: string) => {
    try {
      const inviteUrl = `${window.location.origin}/invite/${inviteCode}`;
      await navigator.clipboard.writeText(inviteUrl);
      setCopiedId(inviteId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Failed to copy invite link');
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // #2946: the badge renders the raw status under `capitalize`, so a
  // snake_case value would read "Opted_out". Map the ones that need words;
  // everything else keeps its existing single-word rendering.
  const getStatusLabel = (status: string) => (status === 'opted_out' ? 'Opted out' : status);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-oro-warning bg-oro-warning/10';
      case 'accepted':
        return 'text-oro-success bg-oro-success/10';
      case 'expired':
        return 'text-text-muted bg-oro-surface-alt';
      case 'revoked':
        return 'text-oro-error bg-oro-error/10';
      case 'opted_out':
        return 'text-text-muted bg-oro-surface-alt';
      default:
        return 'text-text-secondary bg-oro-surface-alt';
    }
  };

  const isLoading = clubLoading || invitesLoading;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Manage Invites" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="max-w-2xl mx-auto p-4 space-y-4">
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-20 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // Error/permission state
  if (clubError || !isLeader || !club) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Manage Invites" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">
              {clubError ? 'Unable to load settings' : 'Only club leaders can manage invites'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Manage Invites" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          {/* Header with Create Button */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-muted">Invite links for {club.name}</p>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/clubs/${slug}/invite`)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create
            </Button>
          </div>

          {/* Invites List */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <Link2 className="w-5 h-5 text-accent" />
              Invites ({invites.length})
            </h2>

            {invites.length === 0 ? (
              <Card className="p-8 text-center">
                <Link2 className="w-8 h-8 text-text-muted mx-auto mb-3" />
                <p className="text-text-secondary font-medium mb-1">No invites yet</p>
                <p className="text-sm text-text-muted mb-4">
                  Create an invite link to grow your club
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/clubs/${slug}/invite`)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Invite
                </Button>
              </Card>
            ) : (
              <div className="space-y-3">
                {invites.map((invite: ClubInvite) => (
                  <div
                    key={invite.id}
                    className="bg-oro-surface border border-oro-border rounded-xl p-4"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-medium text-text-primary capitalize">
                            {invite.type.replace('_', ' ')}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${getStatusColor(invite.status)}`}>
                            {getStatusLabel(invite.status)}
                          </span>
                          {/* Multi-use badge */}
                          {(invite.max_uses === null || invite.max_uses > 1) && (
                            <span className="text-xs px-2 py-1 rounded-full font-medium bg-oro-club/10 text-oro-club">
                              Reusable
                            </span>
                          )}
                        </div>

                        {/* Usage stats for multi-use invites */}
                        {(invite.max_uses === null || invite.max_uses > 1) && (
                          <div className="flex items-center gap-1.5 mb-2">
                            <Users className="w-3.5 h-3.5 text-text-muted" />
                            <span className="text-sm text-text-secondary">
                              {invite.use_count} accepted
                              {invite.max_uses !== null && (
                                <span className="text-text-muted">
                                  {' '}• {invite.max_uses - invite.use_count} remaining
                                </span>
                              )}
                              {invite.max_uses === null && (
                                <span className="text-text-muted"> • Unlimited</span>
                              )}
                            </span>
                          </div>
                        )}

                        {/* Contact Info */}
                        {(invite.invitee_email || invite.invitee_phone) && (
                          <div className="flex items-center gap-1.5 mb-2">
                            {invite.invitee_email ? (
                              <>
                                <Mail className="w-3.5 h-3.5 text-text-muted" />
                                <span className="text-sm text-text-secondary">{invite.invitee_email}</span>
                              </>
                            ) : (
                              <>
                                <Phone className="w-3.5 h-3.5 text-text-muted" />
                                <span className="text-sm text-text-secondary">{invite.invitee_phone}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCopyLink(invite.id, invite.code)}
                          className="p-2 hover:bg-oro-surface-alt rounded-lg transition-colors"
                          title="Copy invite link"
                        >
                          {copiedId === invite.id ? (
                            <Check className="w-4 h-4 text-oro-success" />
                          ) : (
                            <Copy className="w-4 h-4 text-text-muted" />
                          )}
                        </button>
                        {isRevocableInvite(invite) && (
                          <button
                            onClick={() => setInviteToRevoke(invite.id)}
                            className="p-2 hover:bg-oro-surface-alt rounded-lg transition-colors"
                            title="Revoke invite"
                          >
                            <Ban className="w-4 h-4 text-text-muted hover:text-oro-error" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Metadata */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>Created {formatDate(invite.created_at)}</span>
                      </div>
                      {invite.expires_at && (
                        <div className="flex items-center gap-1">
                          <span>Expires {formatDate(invite.expires_at)}</span>
                        </div>
                      )}
                      {(invite.inviter_first_name || invite.inviter_last_name) && (
                        <div className="flex items-center gap-1">
                          <span>by {`${invite.inviter_first_name || ''} ${invite.inviter_last_name || ''}`.trim()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Revoke Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!inviteToRevoke}
        title="Revoke Invite"
        message="Are you sure you want to revoke this invite? The link will no longer work."
        confirmLabel="Revoke"
        cancelLabel="Keep"
        variant="destructive"
        isPending={revokeMutation.isPending}
        onConfirm={() => inviteToRevoke && revokeMutation.mutate(inviteToRevoke)}
        onCancel={() => setInviteToRevoke(null)}
      />
    </div>
  );
}
